(function () {
	"use strict";

	if (window.__partifyVideoTrackingReady) return;
	window.__partifyVideoTrackingReady = true;

	const progressMilestones = [25, 50, 75];
	const boundTrackingElements = new WeakSet();
	const boundPlayers = new WeakSet();
	const playerStates = new WeakMap();
	const clickedKeys = new WeakMap();

	function numberOrNull(value) {
		const number = Number(value);
		return Number.isFinite(number) ? number : null;
	}

	function rounded(value) {
		return Number.isFinite(value) ? Math.round(value * 10) / 10 : null;
	}

	function trackingElementFor(element) {
		if (!element || !element.closest) return element;
		return element.closest("[data-video-track]") || element;
	}

	function contextFor(element) {
		const trackingElement = trackingElementFor(element);
		const productContext =
			element.closest && element.closest("[data-video-product-context]");
		const video =
			element.tagName === "VIDEO"
				? element
				: trackingElement && trackingElement.querySelector
					? trackingElement.querySelector("video")
					: null;
		const source = video && video.querySelector("source");
		const variantInput = document.querySelector(
			'form[action*="/cart/add"] [name="id"]',
		);

		return {
			video_id: trackingElement.dataset.videoId || element.dataset.videoId || "",
			video_title:
				trackingElement.dataset.videoTitle || element.dataset.videoTitle || "",
			video_placement:
				trackingElement.dataset.videoPlacement ||
				element.dataset.videoPlacement ||
				"",
			video_position: numberOrNull(
				trackingElement.dataset.videoPosition ||
					element.dataset.videoPosition ||
					"",
			),
			video_url:
				(video && video.currentSrc) ||
				(source && source.getAttribute("src")) ||
				"",
			product_id: productContext ? productContext.dataset.productId || "" : "",
			variant_id:
				(variantInput && variantInput.value) ||
				(productContext ? productContext.dataset.variantId || "" : ""),
			product_handle: productContext
				? productContext.dataset.productHandle || ""
				: "",
			product_title: productContext
				? productContext.dataset.productTitle || ""
				: "",
			product_type: productContext
				? productContext.dataset.productType || ""
				: "",
			template_name: productContext
				? productContext.dataset.templateName || ""
				: "",
			template_suffix: productContext
				? productContext.dataset.templateSuffix || ""
				: "",
			page_path: window.location.pathname,
			page_url: window.location.href,
		};
	}

	function contextKey(element) {
		const context = contextFor(element);
		return [
			context.video_id,
			context.video_placement,
			context.video_position,
		].join("|");
	}

	function saveEngagement(eventName, properties) {
		if (eventName === "partify_video_impression") return;

		try {
			const storageKey = "partify_video_engagements";
			const stored = JSON.parse(window.sessionStorage.getItem(storageKey) || "[]");
			const engagements = Array.isArray(stored) ? stored : [];
			engagements.push({
				event: eventName,
				video_id: properties.video_id,
				video_placement: properties.video_placement,
				product_id: properties.product_id,
				variant_id: properties.variant_id,
				progress_percent: properties.progress_percent || null,
				timestamp: properties.event_timestamp,
			});
			window.sessionStorage.setItem(
				storageKey,
				JSON.stringify(engagements.slice(-20)),
			);
		} catch (error) {
			return;
		}
	}

	function emit(eventName, element, details) {
		const properties = Object.assign(
			contextFor(element),
			details || {},
			{ event_timestamp: new Date().toISOString() },
		);

		window.igEvents = window.igEvents || [];
		window.igEvents.push({ event: eventName, properties: properties });
		saveEngagement(eventName, properties);
		window.dispatchEvent(
			new CustomEvent("partify:video-analytics", {
				detail: { event: eventName, properties: properties },
			}),
		);
	}

	function bindTrackingElement(element) {
		if (!element || boundTrackingElements.has(element)) return;
		boundTrackingElements.add(element);

		if (element.dataset.videoTrackClick !== "false") {
			element.addEventListener("click", function () {
				const key = contextKey(element);
				if (clickedKeys.get(element) === key) return;
				clickedKeys.set(element, key);
				emit("partify_video_click", element, {
					interaction_type:
						element.dataset.videoTrack === "card" ? "open" : "player",
				});
			});
		}

		if (
			element.dataset.videoTrackImpression !== "false" &&
			"IntersectionObserver" in window
		) {
			const observer = new IntersectionObserver(
				function (entries) {
					entries.forEach(function (entry) {
						if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
						emit("partify_video_impression", element, {
							visible_percent: Math.round(entry.intersectionRatio * 100),
						});
						observer.unobserve(element);
					});
				},
				{ threshold: [0.5] },
			);
			observer.observe(element);
		}
	}

	function playerIsActive(video) {
		if (video.dataset.videoTrackRequiresActivation !== "true") return true;
		return video.dataset.videoAnalyticsActive === "true";
	}

	function stateFor(video) {
		const key = contextKey(video);
		let state = playerStates.get(video);

		if (!state || state.key !== key) {
			state = {
				key: key,
				started: false,
				completed: false,
				milestones: new Set(),
			};
			playerStates.set(video, state);
		}

		return state;
	}

	function playbackDetails(video, percent) {
		return {
			current_time_seconds: rounded(video.currentTime),
			duration_seconds: rounded(video.duration),
			progress_percent: percent,
		};
	}

	function bindPlayer(video) {
		if (!video || boundPlayers.has(video)) return;
		boundPlayers.add(video);

		video.addEventListener("play", function () {
			if (!playerIsActive(video)) return;
			const state = stateFor(video);
			if (state.started) return;
			state.started = true;
			emit("partify_video_start", video, playbackDetails(video, 0));
		});

		video.addEventListener("timeupdate", function () {
			if (!playerIsActive(video) || !Number.isFinite(video.duration)) return;
			if (video.duration <= 0) return;

			const state = stateFor(video);
			const percent = Math.min(
				100,
				Math.floor((video.currentTime / video.duration) * 100),
			);

			progressMilestones.forEach(function (milestone) {
				if (percent < milestone || state.milestones.has(milestone)) return;
				state.milestones.add(milestone);
				emit(
					"partify_video_progress",
					video,
					playbackDetails(video, milestone),
				);
			});

			if (video.loop && percent >= 95 && !state.completed) {
				state.completed = true;
				emit("partify_video_complete", video, playbackDetails(video, 100));
			}
		});

		video.addEventListener("ended", function () {
			if (!playerIsActive(video)) return;
			const state = stateFor(video);
			if (state.completed) return;
			state.completed = true;
			emit("partify_video_complete", video, playbackDetails(video, 100));
		});
	}

	function bind(root) {
		Array.prototype.slice
			.call(root.querySelectorAll("[data-video-track]"))
			.forEach(bindTrackingElement);
		Array.prototype.slice
			.call(root.querySelectorAll("video[data-video-player]"))
			.forEach(bindPlayer);
	}

	function boot() {
		bind(document);
		document.addEventListener("shopify:section:load", function (event) {
			bind(event.target);
		});
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", boot);
	} else {
		boot();
	}
})();
